/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import { formatUTCDate } from '../../utils/utils';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import FreeAccessTab from '../user-detail/tabs/FreeAccessTab';
import StatusPill from '../users/StatusPill';
import GrantAccessModal from '../free-grants/GrantAccessModal';
import createGrantApi, { grantedMessage } from '../free-grants/grantApi';
import { surveyReward } from './surveyRewards';
import '../active-users/active-users.css';
import '../free-grants/free-grants.css';
// Carries the tab strip (.ud-tab*) and the grant-history card styles the Free access tab needs.
import '../user-detail/user-detail.css';
import './survey-responses.css';

/**
 * One survey response, as a PAGE — `createResponsePage(config)` builds the component for ONE
 * questionnaire (`/survey-response/:id` and `/signup-survey-response/:id`).
 *
 * Parameterised for the same reason `createSurveyResponsesService` is on the backend: the two
 * questionnaires share this shape exactly and share no question. What differs is the endpoint,
 * where Back goes, what the breadcrumb says, and which derived answers belong in the member
 * panel — so those, and only those, are config.
 *
 * ── WHY THIS IS NOT A MODAL ──────────────────────────────────────────────────
 *
 * It was one, briefly. A modal cannot be linked to, refreshed, opened in a second tab, or sent
 * to a colleague — and a member's full answers plus an action that changes what they are charged
 * is exactly the thing an operator wants to send to a colleague. The page also has room to show
 * the answers and the member's own facts at once without the cramped scroll a dialog forces.
 *
 * The consequence that mattered in the build: **the page has no list behind it to borrow from.**
 * A modal was opened from a row and could be handed that row; a pasted URL has nothing. So
 * `GET /admin/api/survey-responses/:id` now returns the member entitlement context, this
 * survey's derived answer columns and the survey title alongside the answers, and this component
 * reads nothing but that one response. See `services/surveyAdminService.js#getResponseDetail`.
 *
 * Everything printed here is server-worded (`free_access_duration` is "90 Days" / "Lifetime",
 * never a count this page does arithmetic on), so the page and the list row cannot disagree.
 *
 * ── TWO TABS ─────────────────────────────────────────────────────────────────
 *
 * **Answers** — what this member said, and the page's subject, so it is what opens.
 * **Free access** — every grant that member has ever been given. That is not a second
 * implementation of the User 360 tab, it IS `user-detail/tabs/FreeAccessTab`: same component,
 * same endpoint, same `status=all` contract, so a grant history cannot come to mean two
 * different things depending on which screen you read it from.
 *
 * The tab is in the URL (`?tab=`) for the reason the whole page exists — a tab you cannot link
 * to or refresh into is a mode, not a location. An unknown value resolves to Answers.
 */

const truthy = (v) => v === true || v === 1 || v === '1' || v === 't' || v === 'true';

const Fact = ({ label, children }) => (
  <div className="srp-fact">
    <span className="srp-fact__k">{label}</span>
    <span className="srp-fact__v">{children}</span>
  </div>
);

/**
 * @param {object} config
 * @param {string} config.endpoint    admin read path, without the id
 * @param {string} config.backTo      the tab this response was opened from
 * @param {string} config.backLabel   wording of the back link
 * @param {string} config.breadcrumb  trail up to (but excluding) this response
 * @param {string} config.route       shown in the corner, matching every other screen
 * @param {object} config.scales      {question_code: max} — a scored question is drawn as a
 *                                    meter over its own scale, never as a bare number
 * @param {string} config.promise     what this survey promised its responders, printed under
 *                                    the grant button so the operator knows what to give
 * @param {string} config.grantSource `survey.code` — decides the wording of the member's push
 * @param {boolean} [config.rewardPerSurvey] the response's own `survey_code` decides the promise,
 *                                    the grant wording and its starting length (105: the Help Us
 *                                    Improve page also shows 30-days survey responses)
 */
export function createResponsePage(config) {
  const {
    endpoint, backTo, backLabel, breadcrumb, route, scales = {}, rewardPerSurvey = false,
  } = config;
  // Per response when the page lists more than one survey; otherwise the page's own config.
  const rewardOf = (detail) => (rewardPerSurvey
    ? surveyReward(detail && detail.survey_code)
    : { source: config.grantSource, promise: config.promise });

  return function ResponsePage() {
  const navigate = useNavigate();
  const { responseId } = useParams();

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const [detail, setDetail] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantNotice, setGrantNotice] = useState('');
  // Bumped after each grant so the Free access tab re-reads. It stays mounted once opened, so
  // without this an operator grants and the history in front of them still says they never did.
  const [grantNonce, setGrantNonce] = useState(0);

  // The URL seeds the tab and mirrors it; local state is what renders. Reading `?tab=` on every
  // render would tie the page to the router's re-render behaviour for something it can own
  // outright — the same split UserDetail uses for its tabs.
  const [search, setSearch] = useSearchParams();
  const [active, setActive] = useState(
    () => (search.get('tab') === 'free-access' ? 'free-access' : 'answers'),
  );
  const selectTab = (key) => {
    setActive(key);
    const next = new URLSearchParams(search);
    if (key === 'answers') next.delete('tab'); else next.set('tab', key);
    // `replace`, so flipping tabs does not bury the way back to the list in history.
    setSearch(next, { replace: true });
  };

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${baseUrl}/admin/api/token`);
        setToken(res.data.accessToken);
        setExpire(jwtDecode(res.data.accessToken).exp);
      } catch (err) {
        if (err.response) navigate('/');
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await axiosJWT.get(`${baseUrl}${endpoint}/${responseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data && res.data.data;
      if (!data) { setStatus('notfound'); return; }
      setDetail(data);
      setStatus('ok');
    } catch (err) {
      // A response id belonging to another questionnaire is a 404 here, not an error — the
      // service scopes the lookup to this survey deliberately.
      setStatus(err?.response?.status === 404 ? 'notfound' : 'error');
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [responseId, token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const handleGranted = (grant) => {
    setGrantOpen(false);
    setGrantNotice(grantedMessage(grant));
    // The entitlement facts on this page are server-derived, so nothing here is right until
    // the response is read again.
    load();
    setGrantNonce((n) => n + 1);
  };

  // The reused tab talks to the API through this, exactly as User 360 hands it one.
  const authGet = (path) => axiosJWT.get(`${baseUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });

  const granted = detail ? truthy(detail.free_access_granted) : false;
  // Every reward this member has had, newest first — a member who answered both surveys can
  // have been paid for both, and the newest alone hid the older one.
  const grantedLabel = detail?.free_access_duration_all || detail?.free_access_duration || null;
  const answers = detail?.answers || [];

  const answered = (values) =>
    (values || []).filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
  const answeredCount = answers.filter((a) => answered(a.values).length).length;

  const initial = (detail?.name || detail?.email || '?').trim().charAt(0).toUpperCase();

  /**
   * One answer. A scored question is printed as its score over its scale with a meter, because
   * a bare "1" reads as bad news out of 5 and good news out of 10 — and the person reading this
   * is about to decide whether to give the member Premium. An unanswered question says so in
   * words rather than trailing off in a dash: on this survey the follow-ups are conditional, so
   * "not answered" is normal and worth stating plainly.
   */
  const renderAnswer = (a) => {
    const values = answered(a.values);
    if (!values.length) return <span className="srp-unanswered">Not answered</span>;

    const max = scales[a.question_code];
    const score = Number(values[0]);
    if (max && Number.isFinite(score)) {
      const pct = Math.max(0, Math.min(100, (score / max) * 100));
      const tone = pct <= 40 ? 'low' : pct <= 70 ? 'mid' : 'high';
      return (
        <span className="srp-score">
          <b>{values[0]}</b>
          <em>/ {max}</em>
          <span className={`srp-meter srp-meter--${tone}`} aria-hidden="true">
            <span style={{ width: `${pct}%` }} />
          </span>
        </span>
      );
    }
    return values.join(', ');
  };

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">
              {breadcrumb} / <b>{detail ? `Response #${detail.response_id}` : 'Response'}</b>
            </span>
            <span className="au-route">{route}</span>
            <span className="au-spacer" />
            <span className="au-back">
              {/* Back to the tab, not to the screen's default — and the tab restores the
                  filters and page it was left on (see SurveyResponsesBody). */}
              <Link to={backTo}>← {backLabel}</Link>
            </span>
          </div>

          {status === 'loading' ? (
            <div className="au-panel"><TableSkeleton rows={6} /></div>
          ) : status === 'error' ? (
            <ErrorState message="We couldn’t load this response. Please try again." onRetry={load} />
          ) : status === 'notfound' ? (
            <div className="srp-notfound" data-testid="srp-notfound">
              <EmptyState
                title="Response not found"
                icon="🔍"
                message="This response does not exist, or it belongs to a different survey."
                hasFilters={false}
              />
              <Link to={backTo} className="au-btn au-btn--ghost">← {backLabel}</Link>
            </div>
          ) : (
            <div data-testid="srp-page">
              <h2 className="au-title sr-title">
                {detail.survey_title || 'Survey response'}
                {detail.survey_label ? (
                  <span
                    className={`sr-survey sr-survey--title ${detail.survey_code === 'help_us_improve_30_days' ? 'sr-survey--30' : 'sr-survey--3m'}`}
                    data-testid="srp-survey-badge"
                  >
                    {detail.survey_label}
                  </span>
                ) : null}
              </h2>
              <p className="sr-subtitle">
                Response #{detail.response_id} · {breadcrumb.split('/').pop().trim()}
              </p>

              {grantNotice && (
                <div className="fg-alert fg-alert--ok sr-grant-notice" role="status" data-testid="srp-grant-notice">
                  {grantNotice}
                  <button type="button" className="fg-btn" onClick={() => setGrantNotice('')}>Dismiss</button>
                </div>
              )}

              {/* WHO answered, and the one action this page exists to make possible — read
                  before anything else, so it leads rather than sitting in a side column. */}
              <section className="au-panel srp-hero" data-testid="srp-summary">
                <div className="srp-hero__id">
                  <span className="srp-avatar" aria-hidden="true">{initial}</span>
                  <div>
                    <div className="srp-hero__name">{detail.name || `User #${detail.user_id}`}</div>
                    <div className="srp-hero__email">{detail.email || 'No email on file'}</div>
                    <div className="srp-hero__chips">
                      <span className="srp-chip">
                        <em>Plan</em>
                        <StatusPill state={detail.subscription_state} trialDays={detail.trial_days_remaining} />
                      </span>
                      <span className="srp-chip">
                        <em>Free access</em>
                        {/* Every length they have been given, not only the newest. */}
                        <span className={`sr-grant ${granted ? 'sr-grant--yes' : 'sr-grant--no'}`}>
                          {granted ? (grantedLabel || 'Granted') : 'Not granted yet'}
                        </span>
                      </span>
                      {granted && detail.free_access_granted_at ? (
                        <span className="srp-chip">
                          <em>Last provided</em>
                          {formatUTCDate(detail.free_access_granted_at)}
                        </span>
                      ) : null}
                      <span className="srp-chip">
                        <em>Submitted</em>
                        {formatUTCDate(detail.submitted_at)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* No member behind the response means nobody to grant to — the same dash the
                    list row shows, and no button at all. */}
                {detail.user_id ? (
                  <div className="srp-hero__cta">
                    <button
                      type="button"
                      className="fg-btn fg-btn--gold srp-grant-btn"
                      onClick={() => setGrantOpen(true)}
                      data-testid="srp-grant-free-access"
                    >
                      Grant Free Access
                    </button>
                    {rewardOf(detail).promise ? <p className="srp-promise">This survey promised {rewardOf(detail).promise}.</p> : null}
                    <Link className="au-link srp-profile-link" to={`/user-detail/${btoa(String(detail.user_id))}`}>
                      Open full profile →
                    </Link>
                  </div>
                ) : null}
              </section>

              <div className="ud-tabs srp-tabs" role="tablist" aria-label="Response sections">
                {[
                  // The keys are the URL contract (`?tab=free-access`) and do not change with
                  // the wording. "Answers" and "Free access" said too little on their own.
                  { key: 'answers', label: 'Survey response' },
                  { key: 'free-access', label: 'Free access history' },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={active === t.key}
                    className={`ud-tab${active === t.key ? ' ud-tab--on' : ''}`}
                    data-testid={`srp-tab-${t.key}`}
                    onClick={() => selectTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {active === 'answers' ? (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">What they told us</h4>
                    {answers.length ? (
                      <span className="srp-answered">{answeredCount} of {answers.length} answered</span>
                    ) : null}
                  </div>
                  {answers.length ? (
                    <ol className="srp-qa" data-testid="srp-answers">
                      {answers.map((a, i) => (
                        <li key={a.question_code}>
                          <div className="srp-qa__q">
                            <span className="srp-qa__n" aria-hidden="true">{i + 1}</span>
                            {a.question_text}
                          </div>
                          <div className="srp-qa__a">{renderAnswer(a)}</div>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="srp-noanswers" data-testid="srp-answers">
                      This response has no stored answers.
                    </p>
                  )}
                </div>
              ) : (
                // Mounted only once its tab is opened — the grant history is a second
                // request, and most visits here are to read what someone wrote.
                <FreeAccessTab
                  key={`fa-${grantNonce}`}
                  userId={detail.user_id}
                  authGet={authGet}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {grantOpen && detail && (
        <GrantAccessModal
          user={{ id: detail.user_id, name: detail.name, email: detail.email }}
          api={createGrantApi(axiosJWT, token, baseUrl)}
          // Names this survey, so the push thanks the member for the one they actually answered.
          source={rewardOf(detail).source}
          defaultDays={rewardOf(detail).defaultDays}
          onClose={() => setGrantOpen(false)}
          onGranted={handleGranted}
        />
      )}
    </div>
  );
};
}

/** "Help Us Improve Pauser" (052). */
export default createResponsePage({
  endpoint: '/admin/api/survey-responses',
  backTo: '/user-feedback?tab=survey',
  backLabel: 'Back to survey responses',
  breadcrumb: 'Feedback / Survey Responses',
  route: '/survey-response',
  grantSource: 'help_us_improve',
  promise: 'three months of Premium',
  // 105: this page also opens 30-days survey responses (same questions) — each response's own
  // survey decides the promise and the grant (see surveyRewards.js).
  rewardPerSurvey: true,
  // The scores are ANSWERS and are read in the answer list. They were restated as tiles above
  // it for a day; two readings of one response on one screen is one too many.
  // Drawn as a meter over its own scale — a bare number tells nobody how good it is.
  scales: { q5_satisfaction: 10 },
});
